// ProgresoCursosSection.js — «Mi progreso»: la foto y el nombre de la persona y,
// debajo, la lista de sus cursos con el avance de cada uno en un desplegable.
//
// No inventa datos: los cursos son los del proyecto (`proyecto.documentos`), el
// avance sale del mismo endpoint que usan las fichas del catálogo y el botón de
// descarga solo aparece cuando esa persona ya tiene el certificado emitido.
// Todo lo visible —textos, colores, medidas, qué se muestra— es configuración.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { mentor2Service, certificadoService, encuestaService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import MisEntregas from '../../entregas/MisEntregas';
import { conI18n } from '../../../utils/i18nTexto';
import { buildBg } from '../bgHelpers';
import AvatarPicker from '../../perfil/AvatarPicker';
import EncuestaModal from '../../EncuestaModal';

const Wrapper = styled.section`
  width: 100%;
  padding: ${p => p.$padding || '50px 2rem 80px'};
  background: ${p => p.$bg || '#ffffff'};
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '1120px'};
  margin: 0 auto;
  padding: 0 1.5rem;
  box-sizing: border-box;
`;

/* ---- Cabecera: foto redonda + nombre ---- */
const Ficha = styled.div`
  text-align: center;
  margin-bottom: ${p => p.$margen || '46px'};
`;

const Nombre = styled.div`
  margin-top: ${p => p.$margen || '18px'};
  color: ${p => p.$color || '#12345c'};
  font-size: ${p => p.$size || '28px'};
  font-weight: ${p => p.$peso || 700};
`;

/* ---- Cabecera de la lista ---- */
const Cabecera = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: ${p => p.$margen || '18px'};
  flex-wrap: wrap;
`;

const TituloLista = styled.h2`
  margin: 0;
  color: ${p => p.$color || '#12345c'};
  font-size: ${p => p.$size || '30px'};
  font-weight: ${p => p.$peso || 700};
  font-family: ${p => p.$font || 'inherit'};
`;

const BtnExpandir = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${p => p.$bg || '#12345c'};
  color: ${p => p.$color || '#ffffff'};
  border: none;
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '10px 22px'};
  font-family: inherit;
  font-size: ${p => p.$size || '13px'};
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
`;

/* ---- Tarjeta de curso ---- */
const Tarjeta = styled.div`
  border: ${p => p.$borde || '1px solid #e4e7ec'};
  border-radius: ${p => p.$radio || '6px'};
  background: ${p => p.$bg || '#ffffff'};
  margin-bottom: ${p => p.$margen || '18px'};
  overflow: hidden;
`;

const Fila = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: ${p => p.$padding || '16px 20px'};
  flex-wrap: wrap;
`;

const Check = styled.span`
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${p => (p.$on ? (p.$color || '#12345c') : 'transparent')};
  border: 2px solid ${p => p.$color || '#12345c'};
  color: #ffffff;
  font-size: 14px;
  line-height: 1;
`;

const NombreCurso = styled.button`
  flex: 1 1 240px;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: ${p => p.$size || '17px'};
  color: ${p => p.$color || '#5b6b7f'};
  cursor: pointer;
  &:hover { text-decoration: underline; }
`;

const Acciones = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const BtnCert = styled.button`
  background: transparent;
  color: ${p => p.$color || '#12345c'};
  border: 1px solid ${p => p.$color || '#12345c'};
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '7px 18px'};
  font-family: inherit;
  font-size: ${p => p.$size || '12px'};
  font-weight: 700;
  letter-spacing: 0.3px;
  cursor: pointer;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const Pill = styled.span`
  background: ${p => p.$bg || '#12345c'};
  color: ${p => p.$color || '#ffffff'};
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '7px 18px'};
  font-size: ${p => p.$size || '12px'};
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const Chevron = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: ${p => p.$bg || '#12345c'};
  color: #ffffff;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transform: rotate(${p => (p.$abierto ? '180deg' : '0deg')});
  transition: transform 0.2s;
`;

const Panel = styled.div`
  background: ${p => p.$bg || '#f1f3f6'};
  padding: ${p => p.$padding || '20px 24px 26px'};
`;

const PanelFila = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  font-size: ${p => p.$size || '12px'};
  color: ${p => p.$color || '#5b6b7f'};
  letter-spacing: 0.3px;
  .fuerte { font-weight: 700; }
`;

const Barra = styled.div`
  margin-top: 10px;
  height: ${p => p.$alto || '7px'};
  border-radius: 999px;
  background: ${p => p.$bg || '#d8dde5'};
  overflow: hidden;
`;

const BarraFill = styled.div`
  height: 100%;
  width: ${p => p.$pct || 0}%;
  background: ${p => p.$color || '#12345c'};
  transition: width 0.3s;
`;

const Vacio = styled.p`
  text-align: center;
  color: ${p => p.$color || '#5b6b7f'};
  font-size: 16px;
`;

const ProgresoCursosSection = ({ config, proyecto, tl }) => {
  const c = config || {};
  const t = (v, def = '') => (tl ? tl(v, def) : (v || def));
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const documentos = useMemo(() => proyecto?.documentos || [], [proyecto]);
  const docIdsKey = documentos.map(d => d.id).join(',');

  const [progreso, setProgreso] = useState({});
  const [lecciones, setLecciones] = useState({});
  const [emitidos, setEmitidos] = useState([]);
  const [abiertos, setAbiertos] = useState({});
  const [bajando, setBajando] = useState(null);

  useEffect(() => {
    if (!user || !docIdsKey) return;
    let vivo = true;
    mentor2Service.progresoProyecto(docIdsKey.split(','))
      .then(res => {
        if (!vivo || !res.data?.success) return;
        setProgreso(res.data.progreso || {});
        setLecciones(res.data.lecciones || {});
      })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user, docIdsKey]);

  // Certificados ya emitidos de esta persona: sin ellos no hay botón de descarga.
  useEffect(() => {
    if (!user || c.mostrar_certificado === false || !proyecto?.id) return;
    let vivo = true;
    certificadoService.miProgreso(proyecto.id)
      .then(res => { if (vivo) setEmitidos(res.data?.emitidos || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [user, proyecto?.id, c.mostrar_certificado]);

  // Qué cursos ve esta persona y en qué orden.
  //
  //  - `cursos_por_perfil` asigna a cada perfil (subrol) los suyos: un general
  //    ve el básico y un especialista el avanzado.
  //  - con `revelar_al_completar`, el resto de cursos del proyecto aparece solo
  //    cuando ya terminó los que tiene asignados.
  //  - sin nada de eso, la lista es la de siempre (`cursos_ids` o el proyecto).
  const cursos = useMemo(() => {
    const porId = new Map(documentos.map(d => [String(d.id), d]));
    const deLista = (ids) => (ids || []).map(String).map(id => porId.get(id)).filter(Boolean);

    const porPerfil = c.cursos_por_perfil || {};
    const asignados = deLista(porPerfil[user?.subrol || ''] || porPerfil.default);
    const base = asignados.length ? asignados : (deLista(c.cursos_ids).length ? deLista(c.cursos_ids) : documentos);
    if (!asignados.length || c.revelar_al_completar !== true) return base;

    // Los asignados siempre; los demás, solo con todos los asignados al 100 %.
    const listos = asignados.every(d => Number(progreso[String(d.id)] || 0) >= 100);
    if (!listos) return asignados;
    const yaEstan = new Set(asignados.map(d => String(d.id)));
    return [...asignados, ...documentos.filter(d => !yaEstan.has(String(d.id)))];
  }, [documentos, c.cursos_ids, c.cursos_por_perfil, c.revelar_al_completar, user?.subrol, progreso]);

  const todosAbiertos = cursos.length > 0 && cursos.every(d => abiertos[String(d.id)]);
  const alternarTodos = () => {
    const next = {};
    if (!todosAbiertos) cursos.forEach(d => { next[String(d.id)] = true; });
    setAbiertos(next);
  };

  const abrirPdf = useCallback(async (certId) => {
    setBajando(certId);
    try {
      const res = await certificadoService.descargarPDF(certId);
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(t(c.texto_error_certificado, 'No se pudo abrir el certificado'));
    }
    setBajando(null);
  }, [c.texto_error_certificado, t]);

  // Si el certificado exige encuesta y aún no está respondida, el servidor
  // niega el PDF: primero se abre la encuesta y el PDF sale al terminarla.
  const [encModal, setEncModal] = useState(null);   // { encuesta, certKey, certId }
  const [encEnviando, setEncEnviando] = useState(false);
  const descargar = useCallback(async (cert) => {
    const key = cert.certificado_key || 'default';
    try {
      const res = await encuestaService.estado(proyecto.id, key);
      if (res.data?.requiere && res.data?.encuesta) {
        setEncModal({ encuesta: res.data.encuesta, certKey: key, certId: cert.certificado_id });
        return;
      }
    } catch (e) { /* si el chequeo falla, se intenta la descarga igual */ }
    abrirPdf(cert.certificado_id);
  }, [proyecto?.id, abrirPdf]);
  const responderEncuesta = async (respuestas) => {
    if (!encModal) return false;
    setEncEnviando(true);
    try {
      await encuestaService.responder(proyecto.id, encModal.certKey, respuestas);
      // La encuesta clásica no tiene pantalla de cierre: el PDF sale ya.
      if (encModal.encuesta?.paso_a_paso !== true) {
        const id = encModal.certId;
        setEncModal(null);
        abrirPdf(id);
      }
      return true;
    } catch (e) {
      toast.error('No se pudo enviar la encuesta');
      return false;
    } finally {
      setEncEnviando(false);
    }
  };

  if (!user) return null;

  const claves = c.certificados_por_curso || {};
  return (
    <Wrapper $bg={buildBg(c, '#ffffff')} $padding={c.padding}>
      <Inner $maxWidth={c.ancho_maximo}>
        {(c.mostrar_avatar !== false || c.mostrar_nombre !== false) && (
          <Ficha $margen={c.margen_ficha}>
            {c.mostrar_avatar !== false && (
              <AvatarPicker
                size={c.avatar_tamano}
                borde={c.avatar_borde}
                fondo={c.avatar_fondo}
                editable={c.permitir_elegir_avatar !== false}
                colorAcento={c.color_avatar_acento || c.color_check}
                textoGaleria={t(c.texto_galeria_avatar, 'Elige tu avatar')}
              />
            )}
            {c.mostrar_nombre !== false && (
              <Nombre $color={c.color_nombre} $size={c.tamano_nombre} $peso={c.peso_nombre} $margen={c.margen_nombre}>
                {user.nombre || ''}
              </Nombre>
            )}
          </Ficha>
        )}

        <Cabecera $margen={c.margen_cabecera}>
          <TituloLista $color={c.color_titulo} $size={c.tamano_titulo} $peso={c.peso_titulo} $font={c.fuente_titulo}>
            {t(c.titulo, 'Tus Cursos')}
          </TituloLista>
          {c.mostrar_expandir !== false && cursos.length > 0 && (
            <BtnExpandir
              type="button"
              onClick={alternarTodos}
              $bg={c.color_boton_expandir}
              $color={c.color_texto_expandir}
              $radio={c.radio_boton_expandir}
              $padding={c.padding_boton_expandir}
              $size={c.tamano_boton_expandir}
            >
              <span>{todosAbiertos ? '⌃' : '⌄'}</span>
              {todosAbiertos ? t(c.texto_contraer, 'Contraer todo') : t(c.texto_expandir, 'Expandir todo')}
            </BtnExpandir>
          )}
        </Cabecera>

        {cursos.length === 0 && (
          <Vacio $color={c.color_texto_vacio}>{t(c.texto_vacio, 'Todavía no tienes cursos asignados.')}</Vacio>
        )}

        {cursos.map(d => {
          const id = String(d.id);
          const pct = Number(progreso[id] || 0);
          const det = lecciones[id] || {};
          const total = Number(det.total || 0);
          const hechas = Number(det.hechas || 0);
          const completo = pct >= 100;
          const abierto = !!abiertos[id];
          const cert = emitidos.find(e => (e.certificado_key || 'default') === (claves[id] || '__ninguno__'));
          const etiqueta = completo
            ? t(c.texto_completado, 'COMPLETADO')
            : (pct > 0 ? t(c.texto_en_curso, 'EN PROGRESO') : t(c.texto_sin_iniciar, 'SIN INICIAR'));
          return (
            <Tarjeta key={id} $borde={c.borde_tarjeta} $radio={c.radio_tarjeta} $bg={c.color_tarjeta} $margen={c.margen_tarjeta}>
              <Fila $padding={c.padding_tarjeta}>
                <Check $on={completo} $color={c.color_check}>{completo ? '✓' : ''}</Check>
                <NombreCurso
                  type="button"
                  onClick={() => navigate(`${c.url_curso || '/mentor'}/${d.id}`, { state: { volverA: location.pathname } })}
                  $size={c.tamano_curso}
                  $color={c.color_curso}
                >
                  {t(conI18n(d, 'titulo'))}
                </NombreCurso>
                <Acciones>
                  {c.mostrar_certificado !== false && cert && (
                    <BtnCert
                      type="button"
                      onClick={() => descargar(cert)}
                      disabled={bajando === cert.certificado_id}
                      $color={c.color_boton_certificado}
                      $radio={c.radio_boton_certificado}
                      $padding={c.padding_boton_certificado}
                      $size={c.tamano_boton_certificado}
                    >
                      {t(c.texto_boton_certificado, 'DESCARGAR CERTIFICADO')}
                    </BtnCert>
                  )}
                  <Pill
                    $bg={completo ? (c.color_estado_completado || c.color_estado) : (c.color_estado || undefined)}
                    $color={c.color_texto_estado}
                    $radio={c.radio_estado}
                    $padding={c.padding_estado}
                    $size={c.tamano_estado}
                  >
                    {etiqueta}
                  </Pill>
                  <Chevron
                    type="button"
                    aria-label={abierto ? 'Contraer' : 'Expandir'}
                    onClick={() => setAbiertos(prev => ({ ...prev, [id]: !prev[id] }))}
                    $abierto={abierto}
                    $bg={c.color_chevron}
                  >
                    ⌄
                  </Chevron>
                </Acciones>
              </Fila>
              {abierto && (
                <Panel $bg={c.color_panel} $padding={c.padding_panel}>
                  <PanelFila $size={c.tamano_panel} $color={c.color_texto_panel}>
                    <span className="fuerte">{t(c.texto_progreso, 'PROGRESO DEL CURSO')}</span>
                    <span>
                      <span className="fuerte">{`${pct}% ${t(c.texto_completado_barra, 'COMPLETADO')}`}</span>
                      {total > 0 && ` ${hechas}/${total} ${t(c.texto_pasos, 'pasos')}`}
                    </span>
                  </PanelFila>
                  <Barra $alto={c.alto_barra} $bg={c.color_barra_fondo}>
                    <BarraFill $pct={pct} $color={c.color_barra} />
                  </Barra>
                </Panel>
              )}
            </Tarjeta>
          );
        })}
      </Inner>
      {/* Entregas de archivos con su estado y nota (solo si ha enviado alguna). */}
      {c.mostrar_entregas !== false && <MisEntregas titulo={t(c.titulo_entregas, 'Mis entregas')} acento={c.color_boton_certificado || proyecto?.color_primario} />}
      {encModal && (
        <EncuestaModal
          encuesta={encModal.encuesta}
          color={c.color_boton_certificado || proyecto?.color_primario || '#0f355b'}
          submitting={encEnviando}
          onSubmit={responderEncuesta}
          onFinalizar={() => { const id = encModal.certId; setEncModal(null); abrirPdf(id); }}
          onClose={() => setEncModal(null)}
        />
      )}
    </Wrapper>
  );
};

export default ProgresoCursosSection;
